// biome-ignore-all lint/suspicious/noConsole: logger module intentionally uses console

import path from 'node:path'
import util from 'node:util'
import { withTraceChild } from '@pkg/shared/trace'
import { tryOrDefault } from '@pkg/shared/utils'
import { tryParseJson } from '@pkg/shared/utils/api.utils'
import { environmentManager } from '@tanstack/react-query'
import { createServerOnlyFn } from '@tanstack/react-start'
import dayjs from 'dayjs'
import pino from 'pino'
import { isDevOrTest } from './env'
import { type CallerInfo, getCaller } from './logger/caller'

interface MessageObject extends Record<string, unknown> {
  time: number | string
  level: number
  msg: string
}

const isLambda = !!process.env.AWS_LAMBDA_FUNCTION_NAME

// Dev-only web.log sink, resolved server-side only. createServerOnlyFn lets the
// TanStack Start compiler strip `./devlog` (and the `node:fs` it imports) from
// the client/PWA bundle — logger.ts is imported there too. Awaited once at
// module load (like logger/caller.ts) so the write sink can call it synchronously.
const loadWebLogSink = createServerOnlyFn(
  (): Promise<(record: unknown) => void> => import('./devlog').then(m => m.appendWebLog),
)
const webLogSink =
  !isLambda && process.env.ICARE_WEB_LOG === '1' ? await tryOrDefault(() => loadWebLogSink()) : undefined

/**
 * How the caller's file:line is rendered on each log entry.
 * - hidden: nothing printed.
 * - link:   a small OSC 8 hyperlink (`↗`) — invisible-ish text, click to jump in IDE terminals.
 * - inline: today's behavior — full path:line printed on its own line.
 */
export type CallerMode = 'hidden' | 'link' | 'inline'

/**
 * How OTEL traceId/spanId are used to visually group consecutive log entries.
 * - off:            no grouping; trace fields rendered like any other context.
 * - stripe:         each line gets a colored left-margin bar; color is stable per traceId.
 * - stripe+header:  same stripe, plus a dim header line on every traceId change carrying trace=… span=….
 */
export type TraceGroupMode = 'off' | 'stripe' | 'stripe+header'

export interface PrettyLogConfig {
  caller: CallerMode
  /** Strip these keys from the per-line context. Values stay in the JSON record. */
  hideFields: string[]
  /** Render context as `k=v k=v` after the message when key count ≤ threshold. */
  inlineSmallContext: boolean
  inlineKeyThreshold: number
  /** Drop noisy ADOT/OTEL collector lifecycle messages (source:'lambda'). */
  hideAdotBootstrap: boolean
  /** Collapse START/END/REPORT/INIT_REPORT into one banner + one summary. */
  summarizeLambdaFrames: boolean
  traceGroup: TraceGroupMode
  /** When in 'stripe+header' mode, also emit a ├ span=… header when only the span changes. */
  traceHeaderOnSpanChange: boolean
}

export const prettyLogAllOn: PrettyLogConfig = {
  caller: 'link',
  hideFields: ['scope', 'source', 'functionName', 'stage', 'traceId', 'spanId', 'environment', 'service', 'requestId'],
  inlineSmallContext: true,
  inlineKeyThreshold: 4,
  hideAdotBootstrap: true,
  summarizeLambdaFrames: true,
  traceGroup: 'stripe+header',
  traceHeaderOnSpanChange: false,
}

export const prettyLogAllOff: PrettyLogConfig = {
  caller: 'inline',
  hideFields: [],
  inlineSmallContext: false,
  inlineKeyThreshold: 4,
  hideAdotBootstrap: false,
  summarizeLambdaFrames: false,
  traceGroup: 'off',
  traceHeaderOnSpanChange: false,
}

let usePlainLog = isDevOrTest
let prettyConfig: PrettyLogConfig = usePlainLog ? prettyLogAllOn : prettyLogAllOff

let prettyConsole: Console = console

export const forcePlainLogging = () => {
  usePlainLog = true
  prettyConfig = prettyLogAllOn
}

// Route every pretty log line to stderr. Use this in CLI entry points whose
// stdout is reserved for command output (e.g. `linear scan` writes markdown to
// stdout for CI to capture into a PR comment). Implies plain logging because
// JSON logs in a CLI are never the right output.
export const forceCliLogging = (consoleToUse?: Console) => {
  forcePlainLogging()
  if (consoleToUse) {
    prettyConsole = consoleToUse
  }
}

export const configurePrettyLog = (patch: Partial<PrettyLogConfig>) => {
  prettyConfig = { ...prettyConfig, ...patch }
}

// Seed list of ADOT/OTEL collector + node-warning lines that drown out the signal
// in CLI runs. Add more as you spot them. Matches `msg` only when source='lambda'.
const ADOT_BORING_MSG = new RegExp(
  [
    '^Setting up own telemetry',
    '^Serving metrics',
    '^Starting (GRPC|HTTP) server',
    '^Starting extensions',
    '^Stopping extensions',
    '^Everything is ready',
    '^Development component',
    '^Starting aws-otel-lambda',
    '^Received SHUTDOWN event',
    '^HTTP Server closed',
    '^Received shutdown request',
    '^Starting shutdown',
    '^Shutdown complete',
    '^done$',
    '^EXTENSION\\s+Name:',
    '^\\(node:\\d+\\) ExperimentalWarning',
    '^\\(Use `node --trace-warnings',
    '^service::telemetry::metrics::address is being deprecated',
    '^Using default config URI',
  ].join('|'),
)

const LAMBDA_FRAME_PREFIX = /^(START|END|REPORT|INIT_START|INIT_REPORT|XRAY) /

const DEFAULT_DEPTH = 6

// Configure how deeply nested objects/arrays are printed in Node.js consoles.
// In Node, console uses util.inspect under the hood which truncates after a default depth (often 2),
// resulting in [Object]/[Array] for deeper structures. We provide a formatter that uses util.inspect
// with a configurable depth while keeping rich, expandable objects in the browser.
function getInspectDepth(): number | undefined {
  // Allow configuration via env var (Node only). Example: LOGGER_INSPECT_DEPTH=10
  try {
    // Guard against browsers/bundlers: process may be undefined

    const env = typeof process !== 'undefined' ? process.env : undefined
    const raw = env?.LOGGER_INSPECT_DEPTH
    if (!raw) {
      return DEFAULT_DEPTH
    }
    const n = Number(raw)
    if (!Number.isNaN(n) && n >= 0) return n
    if (raw.toLowerCase() === 'infinity') return Number.POSITIVE_INFINITY
    return DEFAULT_DEPTH
  } catch {
    return DEFAULT_DEPTH
  }
}

function formatLogValue(value: unknown): unknown {
  if (!environmentManager.isServer()) {
    return value
  }
  const depth = getInspectDepth()
  return util.inspect(value, {
    depth,
    maxArrayLength: Infinity,
    breakLength: 120,
    colors: true,
    compact: false,
  })
}

// Simple ANSI color helpers (avoid extra deps)
const colors = {
  reset: '\x1b[0m',
  dim: '\x1b[2m',
  gray: '\x1b[90m',
  blue: '\x1b[34m',
  cyan: '\x1b[36m',
  green: '\x1b[32m',
  yellow: '\x1b[33m',
  red: '\x1b[31m',
  magenta: '\x1b[35m',
}

function colorForLevel(level: number): string {
  if (level >= 60) return colors.magenta // fatal
  if (level >= 50) return colors.red // error
  if (level >= 40) return colors.yellow // warn
  if (level >= 30) return colors.green // info
  if (level >= 20) return colors.cyan // debug
  return colors.gray // trace/default
}

function buildOpenSourceUrl(caller: CallerInfo): string | undefined {
  if (!caller || typeof caller !== 'object') return undefined

  const { file, line, column } = caller

  if (typeof file !== 'string' || file.length === 0) return undefined
  if (typeof line !== 'number' || typeof column !== 'number') return undefined

  if (!environmentManager.isServer()) {
    const absFile = file
      .replace('http://localhost:3000/@fs', '')
      .replace('file://', '')
      // Strip the vite cache-bust query (`?t=...`) and any other query suffix.
      .replace(/\?.*/, '')

    // Send the absolute path as-is; our custom /__os middleware in apps/web/vite.config.ts
    // passes it through `path.resolve(workspaceRoot, …)`, which accepts absolute inputs
    // verbatim. Encode the source value because browsers refuse to navigate URLs whose
    // query contains unencoded `:` (curl tolerates it; real-world clients balk).
    return `http://localhost:3000/__os?source=${encodeURIComponent(`${absFile}:${line}:${column}`)}`
  } else {
    const normalizedFile = file
      // Playwright adds the file:// again
      .replace('file://', '')
    return `file://${normalizedFile}:${line}:${column}`
  }
}

function consoleForLevel(level: number): (...args: unknown[]) => void {
  const method = (() => {
    switch (level) {
      case 10:
        return prettyConsole.trace || prettyConsole.debug || prettyConsole.log
      case 20:
        return prettyConsole.debug || prettyConsole.log
      case 30:
        return prettyConsole.info || prettyConsole.log
      case 40:
        return prettyConsole.warn || prettyConsole.log
      case 50:
      case 60:
        return prettyConsole.error || prettyConsole.log
      default:
        return prettyConsole.log
    }
  })()
  // Bind so the underlying Console instance keeps `this` even when the method
  // is invoked as a free function via the `consoleMethod` variable.
  return method.bind(prettyConsole)
}

function isPrimitive(v: unknown): boolean {
  return v === null || (typeof v !== 'object' && typeof v !== 'function')
}

function relativeToCwd(file: string): string {
  try {
    // Prefer an explicit workspace root (set by the SST dev wrapper) over process.cwd().
    // In the SST dev Lambda worker the cwd isn't the repo root, so path.relative would
    // emit `..`-prefixed paths and the function would bail to absolute. Production
    // Lambdas don't have WORKSPACE_ROOT set, so they fall back to cwd cleanly.
    const root = process.env.WORKSPACE_ROOT ?? process.cwd()
    const abs = file.replace(/^file:\/\//, '')
    const rel = path.relative(root, abs)
    if (!rel || rel.startsWith('..') || path.isAbsolute(rel)) return abs
    return rel
  } catch {
    return file
  }
}

// 256-color palette for trace grouping. Deliberately avoids the 4 ANSI colors
// already used by level prefixes (red/yellow/green/cyan) to keep the stripe
// distinguishable from `[INFO]`/`[WARN]`/etc.
const TRACE_PALETTE = [
  '\x1b[38;5;39m', // soft blue
  '\x1b[38;5;141m', // soft purple
  '\x1b[38;5;208m', // soft orange
  '\x1b[38;5;43m', // soft teal
  '\x1b[38;5;213m', // pink
  '\x1b[38;5;220m', // muted gold
] as const

function colorForTrace(traceId: string): string {
  let h = 0
  for (let i = 0; i < traceId.length; i++) h = (h * 31 + traceId.charCodeAt(i)) | 0
  return TRACE_PALETTE[Math.abs(h) % TRACE_PALETTE.length] ?? ''
}

// Module-level state for eager trace-group rendering. A "group" is consecutive
// log entries sharing a traceId. We never emit a closer (`└`) because in a
// streaming pretty printer we can't know when a group ends — the next line
// might still belong to it.
let lastTraceId: string | undefined
let lastSpanId: string | undefined

/**
 * Parse a Lambda framing line into a compact one-line render.
 * Returns null if the line isn't a frame we know how to summarize.
 */
function summarizeFrame(msg: string): string | null {
  const start = msg.match(/^START RequestId: ([0-9a-f-]+)/i)
  if (start) return `${colors.dim}▶ Lambda ${start[1]}${colors.reset}`

  // END is the silent partner of REPORT — the same RequestId follows in REPORT.
  if (msg.startsWith('END RequestId: ')) return ''

  if (msg.startsWith('REPORT RequestId: ')) {
    const reqId = msg.match(/RequestId: ([0-9a-f-]+)/i)?.[1] ?? '?'
    const dur = msg.match(/\bDuration: ([\d.]+ ms)/)?.[1]
    const billed = msg.match(/\bBilled Duration: ([\d.]+ ms)/)?.[1]
    const maxMem = msg.match(/\bMax Memory Used: ([\d.]+ MB)/)?.[1]
    const status = msg.match(/\bStatus: (\w+)/)?.[1]
    const fields = [
      dur && `duration=${dur}`,
      billed && `billed=${billed}`,
      maxMem && `maxMem=${maxMem}`,
      status && `status=${status}`,
    ]
      .filter(Boolean)
      .join(' ')
    return `${colors.dim}◀ Lambda ${reqId}  ${fields}${colors.reset}`
  }

  if (msg.startsWith('INIT_REPORT ')) {
    const dur = msg.match(/\bInit Duration: ([\d.]+ ms)/)?.[1]
    const phase = msg.match(/\bPhase: (\w+)/)?.[1]
    const status = msg.match(/\bStatus: (\w+)/)?.[1]
    const fields = [dur && `duration=${dur}`, phase && `phase=${phase}`, status && `status=${status}`]
      .filter(Boolean)
      .join(' ')
    return `${colors.dim}● Init  ${fields}${colors.reset}`
  }

  // INIT_START / XRAY — known frames but no useful summary
  if (msg.startsWith('INIT_START ') || msg.startsWith('XRAY ')) return ''

  return null
}

function writeAsPrettyMessage(message: MessageObject) {
  const { level, time, msg, ...rest } = message

  // 1. Boring ADOT/OTEL bootstrap noise from the Lambda tail — drop entirely.
  if (
    prettyConfig.hideAdotBootstrap &&
    rest.source === 'lambda' &&
    typeof msg === 'string' &&
    ADOT_BORING_MSG.test(msg)
  ) {
    return
  }

  const timestamp = dayjs(time).format('HH:mm:ss')
  const levelLabel = (pino.levels.labels[level] ?? 'info').toUpperCase()
  const lvlColor = colorForLevel(level)
  const tsColored = `${colors.dim}${timestamp}${colors.reset}`
  const lvlColored = `${lvlColor}[${levelLabel}]${colors.reset}`

  // Trace grouping — eager: emit header on traceId change, prepend a colored
  // stripe to every line that has a traceId. Lines without a traceId render
  // with a blank stripe slot (keeps message column aligned with grouped lines).
  const traceId = typeof rest.traceId === 'string' ? rest.traceId : undefined
  const spanId = typeof rest.spanId === 'string' ? rest.spanId : undefined
  let stripe = ''
  if (prettyConfig.traceGroup !== 'off') {
    if (traceId) {
      const traceColor = colorForTrace(traceId)
      const traceChanged = traceId !== lastTraceId
      const spanChanged = spanId !== lastSpanId
      if (prettyConfig.traceGroup === 'stripe+header') {
        if (traceChanged) {
          prettyConsole.log(
            `                ${traceColor}┌─ trace=${traceId.slice(0, 8)}${spanId ? `  span=${spanId.slice(0, 8)}` : ''}${colors.reset}`,
          )
        } else if (spanChanged && spanId && prettyConfig.traceHeaderOnSpanChange) {
          prettyConsole.log(`                ${traceColor}├─ span=${spanId.slice(0, 8)}${colors.reset}`)
        }
      }
      stripe = `${traceColor}▎${colors.reset} `
      lastTraceId = traceId
      lastSpanId = spanId
    } else {
      // No traceId on this line — render an empty stripe slot to keep columns aligned.
      stripe = '  '
    }
  }

  // 2. Lambda framing — collapse to a one-line banner/summary card.
  if (
    prettyConfig.summarizeLambdaFrames &&
    rest.frame === true &&
    typeof msg === 'string' &&
    LAMBDA_FRAME_PREFIX.test(msg)
  ) {
    const summary = summarizeFrame(msg)
    if (summary === '') return // silent (END / INIT_START / XRAY)
    if (summary !== null) {
      consoleForLevel(level)(`${tsColored} ${lvlColored} ${stripe}${summary}`)
      return
    }
    // Unknown frame — fall through to default rendering.
  }

  const prettyMessage = `${tsColored} ${lvlColored} ${stripe}${msg}`

  // Caller rendering:
  // - 'hidden': nothing.
  // - 'link':   dim `path:line` suffix on the same line; IntelliJ/VS Code/iTerm
  //             auto-detect path:line patterns and make them clickable.
  // - 'inline': legacy second line with the full path:line:col (and a __tsd link on the client).
  const caller = prettyConfig.caller !== 'hidden' ? getCaller() : undefined
  const sourceUrl = caller && isDevOrTest ? buildOpenSourceUrl(caller) : undefined
  // Build the caller suffix separately so we can append it AFTER inline tags, keeping the
  // clickable target as the rightmost element on the line. Browser: bare __tsd URL (no ANSI —
  // Chrome would render the escapes as literal characters and break the link). Server: dim
  // repo-relative `path:line` for IntelliJ/iTerm pattern matching.
  const inBrowser = typeof window !== 'undefined'
  let callerSuffix = ''
  if (caller && prettyConfig.caller === 'link') {
    callerSuffix =
      inBrowser && sourceUrl
        ? ` ${sourceUrl}`
        : ` ${colors.gray}${relativeToCwd(<string>caller.file)}:${caller.line}${colors.reset}`
  }

  let prettyMessageWithCaller = prettyMessage
  if (caller && prettyConfig.caller === 'inline') {
    prettyMessageWithCaller = sourceUrl
      ? `${prettyMessage}\n${sourceUrl}`
      : `${prettyMessage}\n${caller.file}:${caller.line}:${caller.column}`
  }

  const consoleMethod = consoleForLevel(level)

  // 3. Strip configured context keys.
  const filtered = Object.fromEntries(Object.entries(rest).filter(([k]) => !prettyConfig.hideFields.includes(k)))
  const entries = Object.entries(filtered)

  if (entries.length === 0) {
    consoleMethod(`${prettyMessageWithCaller}${callerSuffix}`)
    return
  }

  // 4. Inline small primitive-only context.
  if (
    prettyConfig.inlineSmallContext &&
    entries.length <= prettyConfig.inlineKeyThreshold &&
    entries.every(([, v]) => isPrimitive(v))
  ) {
    const inline = entries
      .map(([k, v]) => `${colors.gray}${k}${colors.reset}=${typeof v === 'string' ? v : String(v)}`)
      .join(' ')
    consoleMethod(`${prettyMessageWithCaller}  ${inline}${callerSuffix}`)
    return
  }

  // 5. Multi-line fallback (today's behavior).
  if (typeof prettyConsole.groupCollapsed === 'function') {
    prettyConsole.groupCollapsed(`${prettyMessageWithCaller}${callerSuffix}`)
    for (const [key, value] of entries) {
      prettyConsole.log(key, formatLogValue(value))
    }
    prettyConsole.groupEnd()
  } else {
    consoleMethod(`${prettyMessageWithCaller}${callerSuffix}`)
    for (const [key, value] of entries) {
      prettyConsole.log(key, formatLogValue(value))
    }
  }
}

// Build Pino configuration based on environment
const _baseStage = process.env.STAGE ?? process.env.SST_STAGE
const _baseService = process.env.SERVICE
const _awsRegion = process.env.AWS_REGION ?? process.env.AWS_DEFAULT_REGION
const functionName = process.env.AWS_LAMBDA_FUNCTION_NAME
const _functionVersion = process.env.AWS_LAMBDA_FUNCTION_VERSION

const appLogger = pino(
  {
    level: process.env.LOG_LEVEL ?? 'info',
    timestamp: pino.stdTimeFunctions.isoTime,
    formatters: {
      level: label => {
        return { level: label.toUpperCase() }
      },
      bindings: () => ({}),
    },
    // Remove pid/hostname; we'll add only what we want via mixin
    base: null,
    mixin() {
      return withTraceChild({
        ...(isLambda
          ? {
              functionName,
            }
          : {}),
      })
    },
    browser: {
      write: writeAsPrettyMessage as never,
    },
  },
  {
    write(msg: string) {
      const val = tryParseJson<MessageObject>(msg, undefined)
      if (val) {
        if (usePlainLog) {
          writeAsPrettyMessage(val)
        } else {
          const { time, level, awsRequestId, msg, ...data } = val
          prettyConsole.log(`${msg ?? ''}\t${JSON.stringify({ msg, ...data })}`)
        }
        // Dev-only side-sink: give the running sst-dev app's server logs a
        // queryable home on disk without pulling node:fs into the client bundle.
        webLogSink?.(val)
      } else {
      }
    },
  },
)

export const logger = appLogger
